import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTrash, FaExclamationTriangle } from "react-icons/fa";

import {
  useGetQuizQuestionDetailsQuery,
  useDeleteQuizQuestionMutation,
} from "../../slices/quizQuestionApiSlice";

const QuizQuestionRemoveScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    data: quizQuestion,
    isLoading,
    error,
  } = useGetQuizQuestionDetailsQuery(id);

  const [deleteQuizQuestion, { isLoading: loadingDelete }] =
    useDeleteQuizQuestionMutation();

  const deleteHandler = async () => {
    try {
      await deleteQuizQuestion(id).unwrap();

      toast.success("Question removed from quiz successfully");

      navigate(`/quizzes/${quizQuestion.quiz}/questions`);
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Failed to remove question",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-red-50 to-orange-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Link
          to={
            quizQuestion
              ? `/quizzes/${quizQuestion.quiz}/questions`
              : "/quizzes"
          }
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 text-blue-600 shadow hover:bg-blue-50 transition"
        >
          ← Back to Quiz Questions
        </Link>

        {isLoading && (
          <div className="rounded-xl bg-blue-100 p-4 text-center text-blue-700">
            Loading question mapping...
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-red-100 p-4 text-center text-red-700">
            {error?.data?.message ||
              error?.error ||
              "Failed to load question mapping"}
          </div>
        )}

        {!isLoading && quizQuestion && (
          <div className="rounded-3xl border border-white/30 bg-white/90 p-8 shadow-2xl backdrop-blur-lg">
            {/* Warning Header */}

            <div className="text-center">
              <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                <FaExclamationTriangle className="text-4xl text-red-600" />
              </div>

              <h1 className="text-3xl font-bold text-gray-800">
                Remove Question
              </h1>

              <p className="mt-3 text-gray-500">
                Are you sure you want to remove this question from the quiz?
              </p>
            </div>

            {/* Question Details */}

            <div className="mt-8 rounded-2xl border bg-gray-50 p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase text-gray-500">
                Question Details
              </h2>

              <p className="font-semibold text-gray-800">
                {quizQuestion.question?.questionText ||
                  quizQuestion.question?.text ||
                  quizQuestion.question?.title ||
                  "Question unavailable"}
              </p>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Order</p>

                  <p className="font-semibold">{quizQuestion.order}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Marks</p>

                  <p className="font-semibold">{quizQuestion.marks}</p>
                </div>
              </div>
            </div>

            {/* Actions */}

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <button
                onClick={deleteHandler}
                disabled={loadingDelete}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:bg-red-700 transition disabled:opacity-60"
              >
                <FaTrash />

                {loadingDelete ? "Removing..." : "Remove Question"}
              </button>

              <Link
                to={`/quizzes/${quizQuestion.quiz}/questions`}
                className="flex-1 rounded-xl border border-gray-300 bg-white py-3.5 text-center text-lg font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizQuestionRemoveScreen;
